import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import uiStyles from "../theme/uiStyles";

const DEFAULT_TIME_ZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

function getDateFromTimestamp(timestamp) {
  if (!timestamp) return null;
  if (timestamp?.toDate) return timestamp.toDate();

  const parsedDate = new Date(timestamp);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function formatTime(date, timeZone) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

function formatDate(date, timeZone) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric"
  }).format(date);
}

function formatLongDate(date, timeZone) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "long",
    day: "numeric",
    year: "numeric"
  }).format(date);
}

function getRelativeLabel(date, timeZone) {
  const now = new Date();

  const entryDate = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(date);
  const todayDate = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(now);

  if (entryDate === todayDate) return "Today";

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const yesterdayDate = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(yesterday);

  if (entryDate === yesterdayDate) return "Yesterday";

  return formatLongDate(date, timeZone);
}

function countBy(items) {
  return items.reduce((acc, item) => {
    acc[item] = (acc[item] || 0) + 1;
    return acc;
  }, {});
}

function getTopEntry(valuesMap) {
  const entries = Object.entries(valuesMap);
  if (!entries.length) return null;

  return entries.sort((a, b) => b[1] - a[1])[0];
}

export default function History() {
  const [logs, setLogs] = useState([]);
  const [timeFilter, setTimeFilter] = useState("all");

  useEffect(() => {
    async function load() {
      const q = query(collection(db, "history"), orderBy("timestamp", "desc"));
      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      setLogs(data);
    }

    load();
  }, []);

  const processedLogs = useMemo(
    () =>
      logs.map((log) => {
        const timeZone = log.timeZone || DEFAULT_TIME_ZONE;
        const parsedDate = getDateFromTimestamp(log.timestamp);

        return {
          ...log,
          timeZone,
          parsedDate
        };
      }),
    [logs]
  );

  const dashboard = useMemo(() => {
    const totalSessions = processedLogs.length;
    const totalSuggestions = processedLogs.reduce(
      (sum, log) => sum + (log.suggestions?.length || 0),
      0
    );

    const cuisines = countBy(
      processedLogs.map((log) => log.cuisine || "Any").filter(Boolean)
    );
    const locations = countBy(
      processedLogs.map((log) => log.location || "Unknown").filter(Boolean)
    );
    const suggestions = countBy(
      processedLogs.flatMap((log) => log.suggestions || []).filter(Boolean)
    );

    const topCuisine = getTopEntry(cuisines);
    const topLocation = getTopEntry(locations);
    const topSuggestion = getTopEntry(suggestions);

    const recentActivity = processedLogs.slice(0, 6);

    const weeklyTrendMap = {};
    processedLogs.forEach((log) => {
      if (!log.parsedDate) return;
      const key = `${formatDate(log.parsedDate, log.timeZone)}`;
      weeklyTrendMap[key] = (weeklyTrendMap[key] || 0) + 1;
    });

    const weeklyTrend = Object.entries(weeklyTrendMap)
      .slice(0, 7)
      .reverse()
      .map(([label, count]) => ({ label, count }));

    return {
      totalSessions,
      totalSuggestions,
      topCuisine,
      topLocation,
      topSuggestion,
      recentActivity,
      weeklyTrend
    };
  }, [processedLogs]);

  const filteredLogs = useMemo(() => {
    const now = new Date();

    return processedLogs.filter((log) => {
      if (!log.parsedDate) return timeFilter === "all";

      const diffMs = now.getTime() - log.parsedDate.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);

      if (timeFilter === "today") {
        return diffDays < 1;
      }

      if (timeFilter === "7days") {
        return diffDays <= 7;
      }

      if (timeFilter === "30days") {
        return diffDays <= 30;
      }

      return true;
    });
  }, [processedLogs, timeFilter]);

  if (!logs.length) {
    return (
      <div style={styles.page}>
        <div style={uiStyles.card}>
          <h2 style={styles.title}>Insights</h2>
          <p style={styles.emptyText}>
            No history yet. Generate a few suggestions to see trends and recent
            activity here.
          </p>
        </div>
      </div>
    );
  }

  const maxTrendCount =
    Math.max(...dashboard.weeklyTrend.map((item) => item.count), 1) || 1;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={styles.title}>Insights</h2>
        <p style={styles.subtitle}>
          A wider view of your recent sessions, patterns, and go-to next bites.
        </p>
      </div>

      <div style={styles.metricsGrid}>
        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>Total sessions</p>
          <strong style={styles.metricValue}>{dashboard.totalSessions}</strong>
        </div>
        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>Suggestions served</p>
          <strong style={styles.metricValue}>{dashboard.totalSuggestions}</strong>
        </div>
        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>Top cuisine</p>
          <strong style={styles.metricValue}>
            {dashboard.topCuisine ? dashboard.topCuisine[0] : "None yet"}
          </strong>
        </div>
        <div style={styles.metricCard}>
          <p style={styles.metricLabel}>Top next bite</p>
          <strong style={styles.metricValue}>
            {dashboard.topSuggestion ? dashboard.topSuggestion[0] : "None yet"}
          </strong>
        </div>
      </div>

      <div style={styles.mainGrid}>
        <div style={uiStyles.card}>
          <h3 style={styles.sectionTitle}>Recent activity</h3>
          <div style={styles.timeline}>
            {dashboard.recentActivity.map((log) => (
              <div key={log.id} style={styles.timelineItem}>
                <div style={styles.timelineDot} />
                <div style={styles.timelineContent}>
                  <div style={styles.timelineHeader}>
                    <strong>{getRelativeLabel(log.parsedDate, log.timeZone)}</strong>
                    <span style={styles.timelineTime}>
                      {log.parsedDate
                        ? `${formatTime(log.parsedDate, log.timeZone)} • ${log.location || "No location"}`
                        : "Unknown time"}
                    </span>
                  </div>
                  <p style={styles.timelineMeta}>
                    Cuisine: {log.cuisine || "Any"} • Texture: {log.texture || "Any"}
                  </p>
                  <div style={styles.pillRow}>
                    {(log.suggestions || []).slice(0, 4).map((suggestion) => (
                      <span key={`${log.id}-${suggestion}`} style={styles.pill}>
                        {suggestion}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.sideColumn}>
          <div style={uiStyles.card}>
            <h3 style={styles.sectionTitle}>Weekly trend</h3>
            <div style={styles.chartList}>
              {dashboard.weeklyTrend.map((item) => (
                <div key={item.label} style={styles.chartRow}>
                  <span style={styles.chartLabel}>{item.label}</span>
                  <div style={styles.chartTrack}>
                    <div
                      style={{
                        ...styles.chartBar,
                        width: `${(item.count / maxTrendCount) * 100}%`
                      }}
                    />
                  </div>
                  <span style={styles.chartValue}>{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={uiStyles.card}>
            <h3 style={styles.sectionTitle}>Quick insights</h3>
            <div style={styles.insightList}>
              <div style={styles.insightItem}>
                <span style={styles.insightLabel}>Most used location</span>
                <strong>{dashboard.topLocation ? dashboard.topLocation[0] : "Unknown"}</strong>
              </div>
              <div style={styles.insightItem}>
                <span style={styles.insightLabel}>Top cuisine count</span>
                <strong>{dashboard.topCuisine ? dashboard.topCuisine[1] : 0}</strong>
              </div>
              <div style={styles.insightItem}>
                <span style={styles.insightLabel}>Top suggestion count</span>
                <strong>{dashboard.topSuggestion ? dashboard.topSuggestion[1] : 0}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ ...uiStyles.card, ...styles.logExplorer }}>
        <div style={styles.logExplorerHeader}>
          <div>
            <h3 style={styles.sectionTitle}>Log explorer</h3>
            <p style={styles.logExplorerSubtle}>
              Filter saved sessions by timeframe and scan the details below.
            </p>
          </div>

          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
          </select>
        </div>

        <div style={styles.logTable}>
          {filteredLogs.slice(0, 12).map((log) => (
            <div key={log.id} style={styles.logRow}>
              <div style={styles.logPrimary}>
                <strong>{getRelativeLabel(log.parsedDate, log.timeZone)}</strong>
                <span style={styles.logMeta}>
                  {log.parsedDate
                    ? `${formatTime(log.parsedDate, log.timeZone)} • ${log.location || "No location"}`
                    : "Unknown time"}
                </span>
              </div>
              <div style={styles.logSecondary}>
                <span>Cuisine: {log.cuisine || "Any"}</span>
                <span>Texture: {log.texture || "Any"}</span>
                <span>Diet: {log.preferences || "None"}</span>
              </div>
              <div style={styles.logPills}>
                {(log.suggestions || []).map((suggestion) => (
                  <span key={`${log.id}-${suggestion}`} style={styles.pill}>
                    {suggestion}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "24px"
  },
  header: {
    marginBottom: 24
  },
  title: {
    marginTop: 0,
    marginBottom: 8,
    fontSize: 34,
    fontWeight: 800,
    color: "#0f172a"
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    lineHeight: 1.6
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 18,
    marginBottom: 24
  },
  metricCard: {
    background: "rgba(255,255,255,0.88)",
    borderRadius: 20,
    padding: 18,
    boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
    border: "1px solid rgba(255,255,255,0.72)",
    backdropFilter: "blur(10px)"
  },
  metricLabel: {
    marginTop: 0,
    marginBottom: 8,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4
  },
  metricValue: {
    fontSize: 24,
    color: "#0f172a"
  },
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 22
  },
  sideColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 18
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: 16,
    fontSize: 20,
    fontWeight: 700,
    color: "#0f172a"
  },
  timeline: {
    display: "flex",
    flexDirection: "column",
    gap: 16
  },
  timelineItem: {
    display: "flex",
    gap: 14
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: "#f25f4c",
    marginTop: 7,
    flexShrink: 0,
    boxShadow: "0 0 0 6px rgba(242,95,76,0.12)"
  },
  timelineContent: {
    flex: 1,
    paddingBottom: 14,
    borderBottom: "1px solid #e2e8f0"
  },
  timelineHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap"
  },
  timelineTime: {
    color: "#64748b",
    fontSize: 13
  },
  timelineMeta: {
    marginTop: 8,
    marginBottom: 10,
    color: "#475569"
  },
  pillRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8
  },
  pill: {
    padding: "7px 12px",
    borderRadius: 999,
    background: "#fff4ef",
    color: "#9a3412",
    fontSize: 13,
    fontWeight: 600
  },
  chartList: {
    display: "flex",
    flexDirection: "column",
    gap: 12
  },
  chartRow: {
    display: "grid",
    gridTemplateColumns: "72px 1fr 28px",
    alignItems: "center",
    gap: 10
  },
  chartLabel: {
    color: "#475569",
    fontSize: 13
  },
  chartTrack: {
    height: 10,
    borderRadius: 999,
    background: "#f1f5f9",
    overflow: "hidden"
  },
  chartBar: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)"
  },
  chartValue: {
    textAlign: "right",
    fontWeight: 700,
    color: "#0f172a"
  },
  insightList: {
    display: "flex",
    flexDirection: "column",
    gap: 14
  },
  insightItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    padding: "14px 16px",
    borderRadius: 16,
    background: "rgba(248,250,252,0.92)",
    border: "1px solid #e2e8f0"
  },
  insightLabel: {
    color: "#475569"
  },
  emptyText: {
    margin: 0
  },
  logExplorer: {
    marginTop: 24
  },
  logExplorerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 18
  },
  logExplorerSubtle: {
    marginTop: 6,
    marginBottom: 0,
    color: "#64748b"
  },
  filterSelect: {
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid #e7d8d3",
    background: "rgba(255,255,255,0.92)",
    fontSize: 14,
    color: "#0f172a",
    minWidth: 180
  },
  logTable: {
    display: "flex",
    flexDirection: "column",
    gap: 14
  },
  logRow: {
    padding: "16px 18px",
    borderRadius: 18,
    background: "rgba(248,250,252,0.92)",
    border: "1px solid #e2e8f0"
  },
  logPrimary: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap"
  },
  logMeta: {
    color: "#64748b",
    fontSize: 13
  },
  logSecondary: {
    display: "flex",
    flexWrap: "wrap",
    gap: 14,
    marginTop: 10,
    color: "#475569",
    fontSize: 14
  },
  logPills: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 12
  }
};
